import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { DefectRefChipsComponent } from '../../../../components/defect-ref-chips/defect-ref-chips.component';
import { ResPillComponent, ResPillStatus } from '../../../../components/res-pill/res-pill.component';
import { ReleasePlanCase } from '../../../../models/release-results.model';
import { ResultStatus } from '../../../../models/test-run.model';
import { TestRunLink, testRunExecutionsLink } from '../../../../utils/test-run-routes';

export interface PlanCaseResult {
  pill: ResPillStatus;
  labelKey: string;
}

const PASSED: PlanCaseResult = { pill: 'passed', labelKey: 'testResults.plaene.status.passed' };
const FAILED: PlanCaseResult = { pill: 'failed', labelKey: 'testResults.plaene.status.failed' };
const BLOCKED: PlanCaseResult = { pill: 'blocked', labelKey: 'testResults.plaene.status.blocked' };
const NOT_RUN: PlanCaseResult = { pill: 'not-run', labelKey: 'testResults.plaene.status.notRun' };

const CASE_RESULT: Partial<Record<ResultStatus, PlanCaseResult>> = { PASSED, FAILED, BLOCKED };
const SCHNELLFILTER: readonly PlanCaseResult[] = [PASSED, FAILED, BLOCKED, NOT_RUN];

/** Letztes Ergebnis eines Testfalls im Plan; alles außer Bestanden/Fehlgeschlagen/Blockiert zählt als nicht ausgeführt. */
export function planCaseResult(status: ResultStatus | null): PlanCaseResult {
  return (status && CASE_RESULT[status]) || NOT_RUN;
}

/** Testfälle eines aufgeklappten Plans mit Schnellfilter (page-release-freigabe.jsx:88-135). */
@Component({
  selector: 'app-plan-faelle-tabelle',
  standalone: true,
  imports: [TranslatePipe, MatIcon, RouterLink, ResPillComponent, DefectRefChipsComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './plan-faelle-tabelle.component.html',
  styleUrl: './plan-faelle-tabelle.component.scss',
})
export class PlanFaelleTabelleComponent {
  readonly projectId = input.required<string>();
  readonly faelle = input.required<ReleasePlanCase[]>();

  private readonly auswahl = signal<ReadonlySet<ResPillStatus>>(new Set());

  protected readonly keineAuswahl = computed(() => this.auswahl().size === 0);

  protected readonly chips = computed(() =>
    SCHNELLFILTER.map(({ pill, labelKey }) => ({
      status: pill,
      labelKey,
      anzahl: this.faelle().filter((f) => planCaseResult(f.status).pill === pill).length,
      aktiv: this.auswahl().has(pill),
    })),
  );

  protected readonly sichtbar = computed(() =>
    this.keineAuswahl()
      ? this.faelle()
      : this.faelle().filter((f) => this.auswahl().has(planCaseResult(f.status).pill)),
  );

  protected readonly caseResult = planCaseResult;

  protected toggle(status: ResPillStatus): void {
    const next = new Set(this.auswahl());
    if (!next.delete(status)) next.add(status);
    this.auswahl.set(next);
  }

  protected zeigeAlle(): void {
    this.auswahl.set(new Set());
  }

  /** Ausführungen des Laufs; ohne Lauf kein Link. */
  protected executionsLink(fall: ReleasePlanCase): TestRunLink | null {
    return fall.laufId ? testRunExecutionsLink(this.projectId(), fall.laufId) : null;
  }
}
